import { DEFAULT_MEETING_WIDGET_SHORTCUT } from '@electron/shared/meetings.types';
import { config } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useRecordHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Hotkey } from '@/components/blocks/Hotkey';
import { Button, Tooltip, XStack, YStack } from '@/components/ui';
import { electronApis } from '@/features/electron-api';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import {
  findShortcutConflict,
  getShortcutDefinition,
  parseShortcutString,
  recordedKeysToCombo,
  setShortcutRecordingActive,
  type ShortcutId,
} from '@/features/shortcuts';
import type { KeyName } from '@/lib/key';
import {
  getDisallowedShortcut,
  getDisallowedShortcutMessage,
  normalizeShortcutCombo,
} from '@/lib/keyboard-shortcuts';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { inElectron, isMac } from '@/utils/capabilities';

import { settingsItemDomId, shortcutSearchId } from '../settings-search-dom';
import { SettingsSection } from '../settings-section';

import { useSaveMeetingWidgetShortcut } from './settings-keyboard-shortcuts.hooks';

function updateShortcutRecording(active: boolean) {
  setShortcutRecordingActive(active);
  if (!inElectron()) return;
  void electronApis.meetings
    .setMeetingWidgetShortcutSuspended(active)
    .catch(() => {
      toast.error("Couldn't update the meeting widget shortcut.");
    });
}

function conflictsWithMeetingWidgetShortcut(combo: string): boolean {
  const meetingWidgetShortcut = settingsStore.meetingWidgetShortcut;
  return (
    inElectron() &&
    !!meetingWidgetShortcut &&
    normalizeShortcutCombo(combo) ===
      normalizeShortcutCombo(meetingWidgetShortcut)
  );
}

type ShortcutItem = {
  id?: ShortcutId;
  keys: readonly KeyName[];
  keysEnd?: readonly KeyName[];
  description: string;
  configurable?: boolean;
  meetingWidgetShortcut?: boolean;
};

type ShortcutGroup = {
  title: string;
  shortcuts: ShortcutItem[];
};

function shortcutItem(
  id: ShortcutId,
  overrides: Partial<Omit<ShortcutItem, 'id'>> = {}
): ShortcutItem {
  const definition = getShortcutDefinition(id);
  return {
    id,
    keys: definition.keys,
    keysEnd: definition.keysEnd,
    description: definition.label,
    ...overrides,
  };
}

const getGeneralShortcuts = (): ShortcutGroup => ({
  title: 'General',
  shortcuts: [
    shortcutItem('app.newChat'),
    shortcutItem('app.commandBar', { configurable: false }),
    shortcutItem('app.openSettings'),
    shortcutItem('app.toggleSidebar'),
  ],
});

const navigationShortcuts: ShortcutGroup = {
  title: 'Navigation',
  shortcuts: [
    shortcutItem('navigation.back'),
    shortcutItem('navigation.forward'),
  ],
};

const tabShortcuts: ShortcutGroup = {
  title: 'Tabs',
  shortcuts: [
    shortcutItem('tabs.new'),
    shortcutItem('tabs.close'),
    shortcutItem('tabs.reopen'),
    shortcutItem('tabs.previous'),
    shortcutItem('tabs.next'),
    shortcutItem('tabs.switch'),
  ],
};

const getChatShortcuts = (
  requireModEnter: boolean,
  showDefaultModeShortcut: boolean
): ShortcutGroup => ({
  title: 'Chat',
  shortcuts: [
    requireModEnter
      ? shortcutItem('chat.send', { keys: ['mod', 'enter'] })
      : shortcutItem('chat.send', { keys: ['enter'] }),
    requireModEnter
      ? { keys: ['enter'], description: 'New line', configurable: false }
      : {
          keys: ['shift', 'enter'],
          description: 'New line',
          configurable: false,
        },
    ...(showDefaultModeShortcut ? [shortcutItem('chat.modeDefault')] : []),
    shortcutItem('chat.modeMax'),
    shortcutItem('chat.modeUltra'),
    shortcutItem('chat.modeQuick'),
    shortcutItem('chat.voiceInput'),
    shortcutItem('chat.cancelVoiceInput'),
    shortcutItem('chat.attachFile'),
    shortcutItem('chat.find', { configurable: false }),
  ],
});

const meetingShortcuts: ShortcutGroup = {
  title: 'Meetings',
  shortcuts: [
    shortcutItem('meetings.toggle'),
    ...(inElectron()
      ? [
          {
            keys: ['control', 'option', 'm'] as const,
            description: 'Expand/collapse meeting widget',
            meetingWidgetShortcut: true,
          },
        ]
      : []),
  ],
};

const contextChatShortcuts: ShortcutGroup = {
  title: 'Context Chat',
  shortcuts: [shortcutItem('contextChat.toggle')],
};

// Terminal-focused shortcuts are handled inside xterm (local-terminal-keys.ts),
// not by the app registry, so they are listed but not rebindable.
const getTerminalShortcuts = (): ShortcutGroup => {
  const mac = isMac();
  const fixed = (
    description: string,
    keys: readonly KeyName[],
    keysEnd?: readonly KeyName[]
  ): ShortcutItem => ({ description, keys, keysEnd, configurable: false });
  return {
    title: 'Terminal',
    shortcuts: [
      shortcutItem('terminal.toggle'),
      fixed('New terminal tab', mac ? ['mod', 't'] : ['ctrl', 'shift', 't']),
      fixed('Close terminal tab', mac ? ['mod', 'w'] : ['ctrl', 'shift', 'w']),
      fixed(
        'Next terminal tab',
        mac ? ['mod', 'shift', 'bracketright'] : ['ctrl', 'tab']
      ),
      fixed(
        'Previous terminal tab',
        mac ? ['mod', 'shift', 'bracketleft'] : ['ctrl', 'shift', 'tab']
      ),
      fixed('Go to terminal tab', mac ? ['mod', '1'] : ['alt', '1'], [
        mac ? 'mod' : 'alt',
        '9',
      ]),
      fixed('Find in terminal', mac ? ['mod', 'f'] : ['ctrl', 'shift', 'f']),
    ],
  };
};

const getHummingbirdShortcuts = (
  openShortcutKeys: KeyName[],
  requireModEnter: boolean
): ShortcutGroup => ({
  title: 'Hover',
  shortcuts: [
    ...(isMac()
      ? [
          {
            keys: openShortcutKeys,
            description: 'Open Hover',
          },
        ]
      : []),
    { keys: ['escape'], description: 'Hide Hover' },
    {
      keys: requireModEnter ? ['mod', 'enter'] : ['enter'],
      description: 'Send message',
    },
    shortcutItem('hummingbird.newChat'),
    shortcutItem('hummingbird.actionsMenu'),
    shortcutItem('hummingbird.captureScreenshot'),
    shortcutItem('hummingbird.toggleMeeting'),
    shortcutItem('hummingbird.toggleApp'),
    shortcutItem('hummingbird.toggleTextSelection'),
    shortcutItem('hummingbird.copyResponse'),
    shortcutItem('hummingbird.pasteResponse'),
    shortcutItem('hummingbird.pasteResponsePlain'),
    shortcutItem('hummingbird.expandToMain'),
  ],
});

const getDictationShortcuts = (startKeys: KeyName[]): ShortcutGroup => ({
  title: 'Dictation',
  shortcuts: [
    {
      keys: startKeys,
      description: 'Start dictating, or insert what you said',
    },
    { keys: ['escape'], description: 'Discard what you said' },
  ],
});

const typeScaleShortcuts: ShortcutGroup = {
  title: 'Type Scale',
  shortcuts: [
    shortcutItem('typeScale.reset'),
    shortcutItem('typeScale.increase'),
    shortcutItem('typeScale.decrease'),
  ],
};

export const KeyboardShortcutsSettings = observer(() => {
  const showHummingbird = isMac();
  const showDictation =
    isMac() && hasFeature(FEATURES.DICTATION) && settingsStore.dictationEnabled;
  const requireModEnterToSend = config.chat.require_mod_enter_to_send;
  const hummingbirdOpenKeys = parseShortcutString(
    settingsStore.hummingbirdShortcut
  );
  const hasShortcutOverrides =
    Object.keys(settingsStore.shortcutSettings).length > 0 ||
    (inElectron() &&
      settingsStore.meetingWidgetShortcut !== DEFAULT_MEETING_WIDGET_SHORTCUT);
  const { saveShortcut, isSaving } = useSaveMeetingWidgetShortcut();
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    if (!confirmReset) return;
    const timeout = setTimeout(() => setConfirmReset(false), 2000);
    return () => clearTimeout(timeout);
  }, [confirmReset]);

  const showTabs = inElectron();

  const shortcutGroups = [
    getGeneralShortcuts(),
    navigationShortcuts,
    ...(showTabs ? [tabShortcuts] : []),
    getChatShortcuts(requireModEnterToSend, inElectron()),
    meetingShortcuts,
    contextChatShortcuts,
    ...(inElectron() && hasFeature(FEATURES.LOCAL_TERMINAL)
      ? [getTerminalShortcuts()]
      : []),
    ...(showHummingbird
      ? [getHummingbirdShortcuts(hummingbirdOpenKeys, requireModEnterToSend)]
      : []),
    // Only when it is actually bound: the hotkey is inert while dictation is switched off.
    ...(showDictation
      ? [
          getDictationShortcuts(
            parseShortcutString(settingsStore.dictationShortcut)
          ),
        ]
      : []),
    typeScaleShortcuts,
  ];
  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Keyboard Shortcuts</SettingsSection.Title>
      </SettingsSection.Header>
      <YStack className="gap-6">
        {shortcutGroups.map((group) => (
          <ShortcutGroupSection key={group.title} group={group} />
        ))}
      </YStack>
      <XStack className="justify-end pt-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={!hasShortcutOverrides || isSaving}
          className={cn(confirmReset && '!text-danger-9 hover:!text-danger-9')}
          onClick={() => {
            if (confirmReset) {
              settingsStore.resetShortcutSettings();
              if (inElectron())
                void saveShortcut(DEFAULT_MEETING_WIDGET_SHORTCUT);
              setConfirmReset(false);
            } else {
              setConfirmReset(true);
            }
          }}
        >
          {confirmReset ? (
            <>
              <Icons.check className="size-3 motion-scale-in-50 motion-opacity-in-0 motion-duration-100" />
              <span className="motion-opacity-in-0 motion-duration-100">
                Confirm?
              </span>
            </>
          ) : (
            <>
              <Icons.restore className="size-3" />
              Reset all
            </>
          )}
        </Button>
      </XStack>
    </SettingsSection.Root>
  );
});

const ShortcutGroupSection = ({ group }: { group: ShortcutGroup }) => {
  return (
    <YStack className="gap-2">
      <SettingsSection.GroupHeader>{group.title}</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        {group.shortcuts.map((shortcut, index) => (
          <ShortcutRow key={`${group.title}-${index}`} shortcut={shortcut} />
        ))}
      </SettingsSection.Group>
    </YStack>
  );
};

const ShortcutRow = observer(({ shortcut }: { shortcut: ShortcutItem }) => {
  const configurable =
    (!!shortcut.id || shortcut.meetingWidgetShortcut) &&
    shortcut.configurable !== false;
  const customCombo = shortcut.id
    ? settingsStore.getShortcutCombo(shortcut.id)
    : undefined;
  const widgetShortcut = shortcut.meetingWidgetShortcut
    ? settingsStore.meetingWidgetShortcut
    : undefined;
  const keys =
    widgetShortcut !== undefined
      ? parseShortcutString(widgetShortcut)
      : customCombo
        ? parseShortcutString(customCombo)
        : shortcut.keys;
  const keysEnd = customCombo ? undefined : shortcut.keysEnd;

  return (
    // registered shortcuts anchor the row for search deep links; static rows
    // (e.g. "New line") have no id and no search entry
    <XStack
      id={
        shortcut.id
          ? settingsItemDomId(shortcutSearchId(shortcut.id))
          : shortcut.meetingWidgetShortcut
            ? settingsItemDomId('shortcuts.meetings.widget')
            : undefined
      }
      className="items-center justify-between gap-x-4 gap-y-2 flex-wrap py-0.5"
    >
      <span className="text-sm text-foreground">{shortcut.description}</span>
      <XStack className="items-center gap-3 shrink-0 ml-auto">
        {configurable && !shortcut.keysEnd ? (
          <ShortcutHotkeyControl shortcut={shortcut} keys={keys} />
        ) : widgetShortcut === '' ? (
          <span className="text-sm text-foreground-subtle">Off</span>
        ) : (
          <ShortcutLabels keys={keys} keysEnd={keysEnd} />
        )}
      </XStack>
    </XStack>
  );
});

const ShortcutLabels = ({
  keys,
  keysEnd,
}: {
  keys: readonly KeyName[];
  keysEnd?: readonly KeyName[];
}) => (
  <XStack className="items-center gap-1.5">
    <Hotkey hotkey={keys} />
    {keysEnd && (
      <>
        <span className="text-xs text-foreground-subtle">–</span>
        <Hotkey hotkey={keysEnd} />
      </>
    )}
  </XStack>
);

const ShortcutHotkeyControl = observer(
  ({
    shortcut,
    keys,
  }: {
    shortcut: ShortcutItem;
    keys: readonly KeyName[];
  }) => {
    const shortcutId = shortcut.id;
    const isWidgetShortcut = shortcut.meetingWidgetShortcut === true;
    const enabled = isWidgetShortcut
      ? !!settingsStore.meetingWidgetShortcut
      : shortcutId
        ? settingsStore.isShortcutEnabled(shortcutId)
        : true;
    const { saveShortcut, isSaving } = useSaveMeetingWidgetShortcut();
    const [recordedKeys, recorder] = useRecordHotkeys();
    const recordedCombo = recordedKeysToCombo(recordedKeys);
    const [flash, setFlash] = useState<'accepted' | 'rejected' | null>(null);

    useEffect(() => {
      if (!flash) return;
      const timer = setTimeout(() => setFlash(null), 700);
      return () => clearTimeout(timer);
    }, [flash]);

    const startRecording = useCallback(() => {
      updateShortcutRecording(true);
      recorder.resetKeys();
      recorder.start();
    }, [recorder]);

    const stopRecording = useCallback(() => {
      recorder.stop();
      recorder.resetKeys();
      updateShortcutRecording(false);
    }, [recorder]);

    useEffect(() => {
      if (!recorder.isRecording) return;
      updateShortcutRecording(true);
      return () => updateShortcutRecording(false);
    }, [recorder.isRecording]);

    useEffect(() => {
      if (!recorder.isRecording) return;

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        stopRecording();
      };

      document.addEventListener('keydown', handleEscape, true);
      return () => document.removeEventListener('keydown', handleEscape, true);
    }, [recorder, stopRecording]);

    useEffect(() => {
      if (!recorder.isRecording || !recordedCombo) return;

      const disallowedShortcut = getDisallowedShortcut(recordedCombo);
      if (disallowedShortcut) {
        toast.error(getDisallowedShortcutMessage(disallowedShortcut));
        setFlash('rejected');
        stopRecording();
        return;
      }

      if (isWidgetShortcut) {
        stopRecording();
        void saveShortcut(recordedCombo).then((accepted) =>
          setFlash(accepted ? 'accepted' : 'rejected')
        );
        return;
      }
      if (!shortcutId) return;

      if (conflictsWithMeetingWidgetShortcut(recordedCombo)) {
        toast.error(
          'The meeting widget already uses that shortcut. Change its shortcut first.'
        );
        setFlash('rejected');
        stopRecording();
        return;
      }

      const conflict = findShortcutConflict(
        recordedCombo,
        shortcutId,
        (id) => settingsStore.getShortcutCombo(id),
        (id) => settingsStore.isShortcutEnabled(id)
      );

      if (conflict) {
        toast.error(
          `${getShortcutDefinition(conflict).label} already uses that shortcut.`
        );
        setFlash('rejected');
      } else {
        settingsStore.setShortcutEnabled(shortcutId, true);
        settingsStore.setShortcutCombo(shortcutId, recordedCombo);
        setFlash('accepted');
      }

      stopRecording();
    }, [
      recordedCombo,
      recorder.isRecording,
      shortcutId,
      isWidgetShortcut,
      saveShortcut,
      stopRecording,
    ]);

    if (!shortcutId && !isWidgetShortcut) return null;

    return (
      <XStack
        className={cn(
          'h-7 w-fit shrink-0 items-center gap-1 rounded-[8px] border bg-surface-hover px-1 transition-colors duration-300',
          flash === 'accepted'
            ? 'border-success-9'
            : flash === 'rejected'
              ? 'border-danger-9'
              : recorder.isRecording
                ? 'border-accent'
                : 'border-border'
        )}
      >
        <Tooltip
          label={recorder.isRecording ? '' : 'Click to Record Shortcut'}
          sideOffset={8}
        >
          <button
            aria-label={`Record ${shortcut.description} shortcut`}
            className={cn(
              'flex min-w-0 items-center gap-1 overflow-hidden rounded-md text-left',
              'outline-none focus-visible:outline-none',
              enabled ? 'text-foreground' : 'text-foreground-subtle'
            )}
            type="button"
            disabled={isSaving}
            onClick={recorder.isRecording ? stopRecording : startRecording}
          >
            {recorder.isRecording ? (
              <span className="w-[5.5rem] truncate pl-1 text-xs text-foreground-subtle">
                Press keys...
              </span>
            ) : enabled ? (
              <Hotkey hotkey={keys} />
            ) : (
              <span className="w-[5.5rem] truncate pl-1 text-xs">
                Record Hotkey
              </span>
            )}
          </button>
        </Tooltip>
        {recorder.isRecording ? (
          <Tooltip label="Cancel" hotkey={['escape']}>
            <button
              aria-label={`Cancel recording ${shortcut.description} shortcut`}
              className="grid size-5 place-items-center rounded-md text-foreground-subtle outline-none hover:bg-secondary-hover hover:text-foreground focus-visible:bg-secondary-hover focus-visible:text-foreground dark:text-foreground-muted dark:hover:text-foreground dark:focus-visible:text-foreground"
              type="button"
              onClick={stopRecording}
            >
              <Icons.cancel className="size-[0.7rem]" />
            </button>
          </Tooltip>
        ) : enabled ? (
          <Tooltip label="Clear Shortcut">
            <button
              aria-label={`Clear ${shortcut.description} shortcut`}
              className="grid size-5 place-items-center rounded-md text-foreground-subtle outline-none hover:bg-secondary-hover hover:text-foreground focus-visible:bg-secondary-hover focus-visible:text-foreground dark:text-foreground-muted dark:hover:text-foreground dark:focus-visible:text-foreground"
              type="button"
              disabled={isSaving}
              onClick={() => {
                if (isWidgetShortcut) {
                  void saveShortcut('');
                } else if (shortcutId) {
                  settingsStore.setShortcutEnabled(shortcutId, false);
                }
              }}
            >
              <Icons.cancel className="size-[0.7rem]" />
            </button>
          </Tooltip>
        ) : (
          <Tooltip label="Reset to default">
            <button
              aria-label={`Reset ${shortcut.description} shortcut to default`}
              className="grid size-5 place-items-center rounded-md text-foreground-subtle outline-none hover:bg-secondary-hover hover:text-foreground focus-visible:bg-secondary-hover focus-visible:text-foreground dark:text-foreground-muted dark:hover:text-foreground dark:focus-visible:text-foreground"
              type="button"
              disabled={isSaving}
              onClick={() => {
                if (isWidgetShortcut) {
                  void saveShortcut(DEFAULT_MEETING_WIDGET_SHORTCUT).then(
                    (accepted) => setFlash(accepted ? 'accepted' : 'rejected')
                  );
                  return;
                }
                if (!shortcutId) return;
                const defaultCombo =
                  getShortcutDefinition(shortcutId).defaultCombo;
                const defaultCombos =
                  typeof defaultCombo === 'string'
                    ? defaultCombo.split(',')
                    : defaultCombo;
                if (defaultCombos.some(conflictsWithMeetingWidgetShortcut)) {
                  toast.error(
                    'The meeting widget uses this default shortcut. Change one of the shortcuts first.'
                  );
                  setFlash('rejected');
                  return;
                }
                settingsStore.setShortcutCombo(shortcutId, undefined);
                settingsStore.setShortcutEnabled(shortcutId, true);
              }}
            >
              <Icons.restore className="size-[0.7rem]" />
            </button>
          </Tooltip>
        )}
      </XStack>
    );
  }
);
